// CQC's published quality-statement taxonomy and available evidence categories.
// These are available categories, NOT six compulsory submissions per statement.
// Per-location reviewed plans, function-sensitive guidance and readiness live
// in cqcProfileModel.js. Supporting categories remain available; exclusions are
// recorded per evidence item. CQC framework versions are explicitly identified.
// Existing source/evidence IDs are retained when service profiles are introduced.
export const EVIDENCE_CATEGORIES = [
  { id: "peoples-experience", label: "People's experience",
    help: "What people who use the service, and their families/carers, say about it — direct feedback, surveys, complaints, compliments.",
    proofExamples: [
      "Feedback surveys, comment cards and thank-you letters (dated and stored)",
      "Complaints log and how each complaint was resolved",
      "Notes from meetings or focus groups with people who use the service and their families",
      "Records of how feedback led to a change (an action plan, a policy update)",
    ],
    resources: [
      { kind: "website", label: "CQC — People's experience of health and care services",
        url: "https://www.cqc.org.uk/guidance-regulation/providers/assessment/evidence-categories/peoples-experience" },
      { kind: "website", label: "CQC — Listening to and involving people",
        url: "https://www.cqc.org.uk/guidance-regulation/providers/assessment/single-assessment-framework/responsive/listening-involving-people" },
      { kind: "website", label: "CQC — Using people's experience in our regulation",
        url: "https://www.cqc.org.uk/about-us/how-we-will-regulate/using-peoples-experience-our-regulation" },
    ] },
  { id: "staff-feedback", label: "Feedback from staff and leaders",
    help: "What staff, managers and leaders say — culture surveys, supervision notes, exit interviews, whistleblowing records.",
    proofExamples: [
      "Staff survey results and any resulting action plan",
      "Supervision, appraisal and exit-interview notes",
      "Records of concerns raised (including whistleblowing) and how they were handled",
      "Notes from staff focus groups or team meetings on this topic",
    ],
    resources: [
      { kind: "website", label: "CQC — Freedom to speak up",
        url: "https://www.cqc.org.uk/guidance-regulation/providers/assessment/single-assessment-framework/well-led/freedom-speak-up" },
      { kind: "document", label: "CQC — Whistleblowing guidance for providers (PDF)",
        url: "https://www.cqc.org.uk/sites/default/files/2023-06/20131107_100495_v5_00_whistleblowing_guidance_for_providers_registered_with_cqc_amended_0.pdf" },
      { kind: "document", label: "CQC — Raising a concern with CQC: quick guide for staff (PDF)",
        url: "https://www.cqc.org.uk/sites/default/files/20200420_Whistleblowing_quick_guide_final_update.pdf" },
      { kind: "website", label: "NHS Staff Survey",
        url: "https://www.nhsstaffsurveys.com/" },
    ] },
  { id: "partner-feedback", label: "Feedback from partners",
    help: "What commissioners, other agencies, GPs, safeguarding boards and other partners say about the service.",
    proofExamples: [
      "Correspondence or meeting notes with commissioners, GPs or other referring services",
      "Safeguarding board or multi-agency review input",
      "Feedback from regulators, professional bodies or accreditation visits",
      "Notes from joint working with other local providers",
    ],
    resources: [
      { kind: "website", label: "SCIE — Safeguarding Adults Boards: checklist and resources",
        url: "https://www.scie.org.uk/safeguarding/adults/safeguarding-adults-boards-checklist-and-resources/" },
      { kind: "website", label: "SCIE — Safeguarding adults in practice",
        url: "https://www.scie.org.uk/safeguarding/adults/practice/" },
      { kind: "website", label: "CQC — Notifications: guidance for providers",
        url: "https://www.cqc.org.uk/guidance-regulation/providers/registration/guidance" },
    ] },
  { id: "observation", label: "Observation",
    help: "What can be directly observed on site — premises, interactions, equipment, records in use.",
    proofExamples: [
      "Internal walk-round / observation checklists, dated and completed",
      "Photos or notes from an environment or equipment check",
      "Records of observed staff-to-person interactions (e.g. care rounds, handovers)",
      "Follow-up actions raised from an observation and whether they were closed",
    ],
    resources: [
      { kind: "website", label: "HSE — Leadership checklist",
        url: "https://www.hse.gov.uk/leadership/checklist.htm" },
      { kind: "website", label: "HSE — Inspectors' toolkit",
        url: "https://www.hse.gov.uk/humanfactors/toolkit.htm" },
      { kind: "website", label: "HSE — Inspection of work equipment",
        url: "https://www.hse.gov.uk/work-equipment-machinery/inspection.htm" },
    ] },
  { id: "processes", label: "Processes",
    help: "The policies, procedures, SOPs and systems that are supposed to be followed — and evidence they exist and are current.",
    proofExamples: [
      "The current, dated policy or SOP itself, with its review date",
      "Records showing staff have read/been trained on it",
      "Audit or spot-check results confirming the process is actually followed",
      "Risk assessments or protocols tied to this area",
    ],
    resources: [
      { kind: "website", label: "CQC — Regulation 17: Good governance",
        url: "https://www.cqc.org.uk/guidance-regulation/providers/regulations-service-providers-and-managers/health-social-care-act/regulation-17" },
      { kind: "website", label: "CQC — Governance, management and sustainability",
        url: "https://www.cqc.org.uk/guidance-regulation/providers/assessment/single-assessment-framework/well-led/governance-management-sustainability" },
      { kind: "website", label: "NICE — Into practice resources",
        url: "https://www.nice.org.uk/implementing-nice-guidance/into-practice-resources" },
      { kind: "website", label: "NICE — Browse all guidance",
        url: "https://www.nice.org.uk/guidance" },
    ] },
  { id: "outcomes", label: "Outcomes",
    help: "Measurable results — audit results, incident trends, outcome data, KPIs tied to this quality statement.",
    proofExamples: [
      "Audit results and trend data over time",
      "Incident/notification statistics and what changed as a result",
      "Outcome measures relevant to this statement (waiting times, clinical outcomes, KPIs)",
      "A summary of what improved after the last review cycle",
    ],
    resources: [
      { kind: "website", label: "NHS England — Learn from patient safety events (LFPSE) service",
        url: "https://www.england.nhs.uk/patient-safety/patient-safety-insight/learning-from-patient-safety-events/learn-from-patient-safety-events-service/" },
      { kind: "website", label: "NHS England — Record a patient safety event",
        url: "https://www.england.nhs.uk/patient-safety/patient-safety-insight/learning-from-patient-safety-events/report-patient-safety-incident/" },
      { kind: "document", label: "CQC — Statutory notifications guidance for providers (PDF)",
        url: "https://www.cqc.org.uk/sites/default/files/20161101_100501_v7_guidance_on_statutory_notifications_ASC_IH_PDC_PA_Reg_Persons.pdf" },
    ] },
];

// SECTOR_WEIGHTING_SEAM: default requirement badge per evidence category id,
// overridable per quality statement below via `elementOverrides`.
const DEFAULT_REQUIREMENT = "optional";

function buildElements(overrides = {}) {
  return EVIDENCE_CATEGORIES.map((ec) => ({
    id: ec.id,
    label: ec.label,
    help: ec.help,
    requirement: (overrides[ec.id] && overrides[ec.id].requirement) || DEFAULT_REQUIREMENT,
    link: (overrides[ec.id] && overrides[ec.id].link) || null,
  }));
}

// CQC handbook / quality-statement guidance, for the item-level "learn more"
// link. CQC restructures these URLs periodically — verify before go-live.
const CQC_GUIDANCE_HOME = "https://www.cqc.org.uk/guidance-providers/single-assessment-framework";

// ---------------------------------------------------------------------------
// The 34 quality statements, grouped under the 5 key questions.
// id / label / description text is CQC's own wording (paraphrased for
// brevity where the official "We statement" is long); each item's evidence
// list is the standard 6 categories unless overridden.
// ---------------------------------------------------------------------------
export const GROUPS = [
  {
    id: "safe",
    label: "Safe",
    link: CQC_GUIDANCE_HOME,
    description: "People are protected from abuse and avoidable harm.",
    items: [
      { id: "learning-culture", label: "Learning culture",
        description: "The service has a proactive and positive culture of safety based on openness and honesty, in which concerns about safety are listened to, safety events are investigated and reported thoroughly, and lessons are learned to continually identify and strengthen safety systems.",
        elements: buildElements() },
      { id: "safe-systems", label: "Safe systems, pathways and transitions",
        description: "The service works with people and healthcare partners to establish and maintain safe systems of care, in which safety is managed, monitored and assured, including at referral, transfer and discharge.",
        elements: buildElements() },
      { id: "safeguarding", label: "Safeguarding",
        description: "The service works with people to understand what being safe means to them, and how to help them enact their own choices to stay safe. It works with adult social care partners to develop a joint approach to safeguarding.",
        elements: buildElements() },
      { id: "involving-people-risks", label: "Involving people to manage risks",
        description: "The service works with people to understand and manage risks, providing care to meet their needs that minimises the possibility of harm.",
        elements: buildElements() },
      { id: "safe-environments", label: "Safe environments",
        description: "The service detects and controls potential risks in the care environment, and makes sure equipment, facilities and technology support safe care.",
        elements: buildElements() },
      { id: "safe-staffing", label: "Safe and effective staffing",
        description: "The service makes sure there are enough qualified, skilled and experienced people, who receive effective support, supervision and development, to meet people's needs and keep them safe.",
        elements: buildElements() },
      { id: "infection-control", label: "Infection prevention and control",
        description: "The service assesses and manages the risk of infection, and detects and controls the risk of it spreading.",
        elements: buildElements() },
      { id: "medicines-optimisation", label: "Medicines optimisation",
        description: "The service makes sure that medicines and treatments are safe and meet people's needs, capacities and preferences.",
        elements: buildElements() },
    ],
  },
  {
    id: "effective",
    label: "Effective",
    link: CQC_GUIDANCE_HOME,
    description: "People's care, treatment and support achieves good outcomes, promotes a good quality of life and is evidence based where possible.",
    items: [
      { id: "assessing-needs", label: "Assessing needs",
        description: "The service maximises the effectiveness of people's care and treatment by assessing and reviewing their health, care, wellbeing and communication needs with them.",
        elements: buildElements() },
      { id: "evidence-based-care", label: "Delivering evidence-based care and treatment",
        description: "The service plans and delivers people's care and treatment with them, including what's important and matters to them, in line with legislation and evidence-based guidance.",
        elements: buildElements() },
      { id: "team-working", label: "How staff, teams and services work together",
        description: "The service works well within itself and with other organisations to deliver effective care and treatment, sharing information as needed for continuity of care.",
        elements: buildElements() },
      { id: "healthy-lives", label: "Supporting people to live healthier lives",
        description: "The service supports people to manage their health and wellbeing so they can maximise their independence, choice and control, and live healthier lives.",
        elements: buildElements() },
      { id: "monitoring-outcomes", label: "Monitoring and improving outcomes",
        description: "The service routinely monitors people's care and treatment to continuously improve it, ensuring outcomes are positive and consistent, and align with expected local and national standards.",
        elements: buildElements() },
      { id: "consent", label: "Consent to care and treatment",
        description: "The service tells people about their rights around consent and respects these when delivering person-centred care and treatment, including when people have limited capacity.",
        elements: buildElements() },
    ],
  },
  {
    id: "caring",
    label: "Caring",
    link: CQC_GUIDANCE_HOME,
    description: "The service involves people and treats them with compassion, kindness, dignity and respect.",
    items: [
      { id: "kindness-dignity", label: "Kindness, compassion and dignity",
        description: "The service treats people with kindness, empathy and compassion, and respects their privacy and dignity. It treats colleagues from other organisations with kindness and respect.",
        elements: buildElements() },
      { id: "individuality", label: "Treating people as individuals",
        description: "The service promotes people's independence, so they know their rights and have choice and control over their own care, and support them to have as much control and independence as possible.",
        elements: buildElements() },
      { id: "independence-choice", label: "Independence, choice and control",
        description: "The service promotes people's independence, so they know their rights and have choice and control over their own care, treatment and wellbeing support.",
        elements: buildElements() },
      { id: "immediate-needs", label: "Responding to people's immediate needs",
        description: "The service listens to and understands people's needs, views and wishes. Staff respond to these immediately and appropriately.",
        elements: buildElements() },
      { id: "workforce-wellbeing", label: "Workforce wellbeing and enablement",
        description: "The service cares about and promotes the wellbeing of staff and people close to them, enabling them to deliver person-centred care.",
        elements: buildElements() },
    ],
  },
  {
    id: "responsive",
    label: "Responsive",
    link: CQC_GUIDANCE_HOME,
    description: "The service meets people's needs.",
    items: [
      { id: "person-centred-care", label: "Person-centred care",
        description: "The service finds out and meets the range and complexity of people's needs and preferences.",
        elements: buildElements() },
      { id: "continuity", label: "Care provision, integration and continuity",
        description: "The service understands the diverse health and care needs of people and their local communities, so care is joined-up, flexible and supports choice and continuity.",
        elements: buildElements() },
      { id: "providing-information", label: "Providing information",
        description: "The service provides appropriate, accurate and up-to-date information in formats that are tailored to individual needs.",
        elements: buildElements() },
      { id: "listening-involving", label: "Listening to and involving people",
        description: "The service makes it easy for people to give feedback and share concerns about their care, involving an independent advocate when needed. It takes account of feedback to shape improvements.",
        elements: buildElements() },
      { id: "equity-access", label: "Equity in access",
        description: "The service makes sure that everyone can access care, support and treatment when they need it, taking account of complexity and urgency of people's needs.",
        elements: buildElements() },
      { id: "equity-outcomes", label: "Equity in experiences and outcomes",
        description: "The service strives to actively understand and improve people's experiences so all people have equitable access, experience and outcomes, taking action to address any inequalities or barriers.",
        elements: buildElements() },
      { id: "planning-future", label: "Planning for the future",
        description: "Where appropriate, the service discusses with people, and their families where relevant, their wishes for their future, including end of life care.",
        elements: buildElements() },
    ],
  },
  {
    id: "well-led",
    label: "Well-led",
    link: CQC_GUIDANCE_HOME,
    description: "The leadership, management and governance of the organisation assures the delivery of high-quality, person-centred care.",
    items: [
      { id: "shared-direction", label: "Shared direction and culture",
        description: "The service has a shared vision, strategy and culture based on transparency, equity, equality and human rights, diversity and inclusion, and engagement.",
        elements: buildElements() },
      { id: "capable-leaders", label: "Capable, compassionate and inclusive leaders",
        description: "The service has inclusive leaders at all levels who understand the context in which they deliver care, treatment and support, and embody the culture and values of their workforce and organisation.",
        elements: buildElements() },
      { id: "freedom-speak-up", label: "Freedom to speak up",
        description: "The service fosters a positive culture where people feel they can speak up and their voice will be heard.",
        elements: buildElements() },
      { id: "workforce-equality", label: "Workforce equality, diversity and inclusion",
        description: "The service values diversity in its workforce and provides an inclusive and supportive environment.",
        elements: buildElements() },
      { id: "governance", label: "Governance, management and sustainability",
        description: "The service has clear responsibilities, roles, systems of accountability and good governance to manage and deliver good quality, sustainable care.",
        elements: buildElements() },
      { id: "partnerships", label: "Partnerships and communities",
        description: "The service understands its duty to collaborate and work in partnership, so services work seamlessly for people, sharing information and learning.",
        elements: buildElements() },
      { id: "learning-improvement", label: "Learning, improvement and innovation",
        description: "The service focuses on continuous learning, innovation and improvement across the organisation and local system.",
        elements: buildElements() },
      { id: "environmental-sustainability", label: "Environmental sustainability, sustainable development",
        description: "The service assesses and mitigates the impact of its practices on the environment, seeking sustainable ways of working.",
        elements: buildElements({
          "outcomes": { requirement: "recommended" },
          "partner-feedback": { requirement: "optional" },
        }) },
    ],
  },
];

// Flat lookup helpers, same shape of utility the pharmacy app uses.
export function findItem(groupId, itemId) {
  const g = GROUPS.find((x) => x.id === groupId);
  if (!g) return null;
  return g.items.find((x) => x.id === itemId) || null;
}

// One place the Worklist (and anything else) pulls "what's this for" /
// "what proof is needed" copy from for a given (group, item, element) —
// purpose is the item's own CQC-derived description; proof is the
// category's general proofExamples (see the header comment above
// EVIDENCE_CATEGORIES for what that list is and isn't).
export function elementGuidance(groupId, itemId, elementId) {
  const group = GROUPS.find((g) => g.id === groupId);
  const item = group && group.items.find((it) => it.id === itemId);
  const category = EVIDENCE_CATEGORIES.find((ec) => ec.id === elementId);
  return {
    groupLabel: group ? group.label : groupId,
    itemLabel: item ? item.label : itemId,
    purpose: item ? item.description : "",
    categoryLabel: category ? category.label : elementId,
    categoryHelp: category ? category.help : "",
    proofExamples: category ? category.proofExamples : [],
    resources: category ? category.resources : [],
  };
}

export function allElementIds() {
  const ids = [];
  for (const g of GROUPS) for (const it of g.items) for (const el of it.elements) {
    ids.push(`${g.id}|${it.id}|${el.id}`);
  }
  return ids;
}

// Total element count — used to sanity-check roll-ups (34 items x 6
// categories = 204 checkable evidence elements per site).
export const TOTAL_ELEMENT_COUNT = GROUPS.reduce(
  (sum, g) => sum + g.items.reduce((s2, it) => s2 + it.elements.length, 0), 0
);

// ---------------------------------------------------------------------------
// deriveSector — a DISPLAY LABEL ONLY, from a site's gacServiceTypes (pulled
// straight from the CQC API — see cqc_client.js's normaliseLocation). Used
// so a mixed-sector organisation's site cards and Inspector view can show
// "Hospital" / "Dental practice" / etc. next to a site's name. This is a
// best-effort keyword match against CQC's own GAC service type names, not an
// authoritative CQC classification and not something the rest of this module
// filters or hides content on — see the header comment above for why
// applicability is handled separately, deliberately, and per-site instead.
// ---------------------------------------------------------------------------
const SECTORS = [
  { key: "hospital", label: "Hospital", match: [/hospital/i, /acute/i, /surgical/i] },
  { key: "dentist", label: "Dental practice", match: [/dental/i, /dentist/i] },
  { key: "care-home", label: "Care home", match: [/care home/i] },
  { key: "domiciliary-care", label: "Domiciliary care", match: [/domiciliary/i, /home care/i, /supported living/i] },
  { key: "gp-practice", label: "GP practice", match: [/general practice/i, /gp practice/i, /doctors? consult/i] },
  { key: "mental-health", label: "Mental health", match: [/mental health/i] },
  { key: "ambulance", label: "Ambulance", match: [/ambulance/i] },
  { key: "hospice", label: "Hospice", match: [/hospice/i] },
];

export function deriveSector(site) {
  const types = (site && site.gacServiceTypes) || [];
  for (const s of SECTORS) {
    if (types.some((t) => s.match.some((re) => re.test(t)))) return { key: s.key, label: s.label };
  }
  return { key: "other", label: types[0] || "Other / general" };
}
